import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import TextField from "@components/ui/textfields/TextField";
import { role_dict } from "@screens/sampleData";
import { useState } from "react";
import { invitationsApi } from "@services/api/invitations";
import { showNotification } from "context/notification.service";
import { colors } from "sexy";

const AVAILABLE_COLORS = [
  { value: colors.blue_800, label: "Bleu foncé" },
  { value: colors.blue_400, label: "Bleu clair" },
  { value: colors.purple_500, label: "Violet" },
  { value: colors.emerald_500, label: "Vert" },
  { value: colors.orange_500, label: "Orange" },
  { value: colors.red_500, label: "Rouge" },
  { value: colors.yellow_500, label: "Jaune" },
  { value: colors.neutral_600, label: "Gris" },
];

export const AddMemberModal = ({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState<string>(Object.keys(role_dict)[0]);
  const [accessType, setAccessType] = useState<string>("MEMBER");
  const [selectedColor, setSelectedColor] = useState<string>(AVAILABLE_COLORS[0].value);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    if (!email) {
      setError("L'adresse e-mail est requise");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Veuillez entrer une adresse e-mail valide");
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      await invitationsApi.create({
        invitee_email: email,
        invitee_name: name || undefined,
        invitation_type: "web",
        guest: false, // Full org member
        expires_in_hours: 168, // 7 days
        context: {
          role,
          access_type: accessType,
          color_tag: selectedColor,
          phone_number: phoneNumber || "",
        },
      });

      showNotification({
        type: "success",
        message: `Invitation envoyée à ${email}`,
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      console.error("Failed to send invitation:", err);
      const errorMessage = "Échec de l'envoi de l'invitation";
      setError(errorMessage);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <BaseModal
      title="Ajouter un membre"
      startIcon={<Icon1 name="plus" size={20} />}
      onClose={onClose}
    >
      <div className="bg-neutral-100 flex flex-col gap-2 p-4">
        {/* Basic Information */}
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Informations du membre</div>
          <TextField
            label="Adresse e-mail *"
            value={email}
            onChange={(value) => setEmail(value)}
            placeholder="marie.curie@example.com"
            type="email"
          />
          <TextField
            label="Nom complet"
            value={name}
            onChange={(value) => setName(value)}
            placeholder="Marie Curie"
          />
          <TextField
            label="Numéro de téléphone (optionnel)"
            value={phoneNumber}
            onChange={(value) => setPhoneNumber(value)}
            placeholder="06 12 34 56 78"
            type="phone"
          />
        </div>

        {/* Role and Access */}
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Rôle et permissions</div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <DropDown
                options={Object.entries(role_dict).map(([key, value]) => ({
                  value: key,
                  label: value,
                }))}
                label="Rôle"
                value={role}
                onSelect={(value) => setRole(String(value))}
              />
            </div>
            <div className="flex-1">
              <DropDown
                options={[
                  { value: "MEMBER", label: "Membre" },
                  { value: "ADMIN", label: "Manager" },
                ]}
                label="Niveau d'accès"
                value={accessType}
                onSelect={(value) => setAccessType(String(value))}
              />
            </div>
          </div>
        </div>

        {/* Color Tag Selection */}
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Couleur du drapeau</div>
          <div className="text-h5 text-neutral-400">Choisissez une couleur pour identifier le membre</div>
          <div className="flex flex-row gap-2 flex-wrap">
            {AVAILABLE_COLORS.map((color) => (
              <div
                key={color.value}
                onClick={() => setSelectedColor(color.value)}
                className={`cursor-pointer rounded-lg border-2 p-3 transition-all ${
                  selectedColor === color.value
                    ? "border-blue-500 ring-2 ring-blue-200"
                    : "border-neutral-200 hover:border-neutral-400"
                }`}
              >
                <div
                  className="w-8 h-8 rounded"
                  style={{ backgroundColor: color.value }}
                  title={color.label}
                />
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-h4 text-red-600">
            {error}
          </div>
        )}
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose} disabled={isSending}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={isSending ? "Envoi en cours..." : "Envoyer l'invitation"}
          onClick={handleSend}
          disabled={isSending}
        />
      </div>
    </BaseModal>
  );
};
